// src/modals/AllListsModal.jsx
import React, { useState } from "react";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSearch,
  faCopy,
  faChevronDown,
  faChevronUp,
  faQuestionCircle,
  faListCheck,faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import ExcelImportExport from "../herpercomp/ExcelImportExport";
import "./styles/AllListsModal.css";

Modal.setAppElement("#root");

const AllListsModal = ({ isOpen, onClose, lists, sdataGroup, onListUpdate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedListId, setExpandedListId] = useState(null);

const eligibleQuestions = React.useMemo(() => {
  if (!sdataGroup) return [];

  return Object.entries(sdataGroup)
    .filter(([key, data]) => {
      if (!data || typeof data !== "object") return false;

      const type = data.type || data.Type || "";
      return type === "SingleResponse" || type === "MultipleResponse";
    })
    .map(([key, data]) => {
      // Auto-detect question number
      const questionNumber =
        data.Question_number ||
        data.question_number ||
        key; // fallback to object key

      return {
        number: String(questionNumber),
        title: data.Qtitle || data.Question_text || "(No title)",
      };
    })
    .sort((a, b) =>
      a.number.localeCompare(b.number, undefined, { numeric: true })
    );
}, [sdataGroup]);

  const filteredLists = React.useMemo(() => {
    if (!searchTerm.trim()) return lists;
    const lower = searchTerm.toLowerCase();
    return lists.filter((list) => {
      const listKey = Object.keys(list)[0];
      if (listKey.toLowerCase().includes(lower)) return true;
      if (list.questionNumbers) {
        const qText = list.questionNumbers
          .map((q) => (typeof q === "object" ? `${q.name} ${q.type}` : String(q)))
          .join(" ")
          .toLowerCase();
        return qText.includes(lower);
      }
      return false;
    });
  }, [lists, searchTerm]);

  // ... keep all your existing helper functions: getTypeBadgeClass, renderQuestionTags, renderListItems, copyListToClipboard, toggleListExpansion

  const getTypeBadgeClass = (list) => {
    if (!list.type) return "mixed";
    const type = list.type.toLowerCase();
    if (type.includes("row")) return "rows";
    if (type.includes("column")) return "columns";
    if (type.includes("statement")) return "statements";
    if (list.questionNumbers) {
      const uniqueTypes = [...new Set(list.questionNumbers.map((q) => q.type))];
      if (uniqueTypes.length > 1) return "mixed";
    }
    return type;
  };

  const renderQuestionTags = (list) => {
    if (!list.questionNumbers || list.questionNumbers.length === 0) {
      return <span className="no-questions">No questions assigned</span>;
    }
    
    return list.questionNumbers.map((q, idx) => {
      const qText = typeof q === 'object' ? `${q.name} (${q.type})` : String(q);
      return (
        <span key={idx} className="question-tag">
          {qText}
        </span>
      );
    });
  };

  const renderListItems = (list) => {
    const listKey = Object.keys(list)[0];
    const listData = list[listKey];
    
    if (!Array.isArray(listData) || listData.length === 0) {
      return <div className="empty-list">No items in this list</div>;
    }
    
    return (
      <ul className="items-list">
        {listData.map((item, idx) => {
          const itemText = item.text || item.title || item.label || "Unnamed item";
          const hasFilter = item.filterLogic && 
            (item.filterLogic.dependent1 !== undefined || 
             item.filterLogic.dependent2 !== undefined);
          
          return (
            <li key={idx} className={`list-item ${hasFilter ? 'has-filter' : ''}`}>
              <span className="item-text">{itemText}</span>
              {hasFilter && (
                <span className="filter-badge" title="Has filter logic">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const copyListToClipboard = (list) => {
    const listKey = Object.keys(list)[0];
    const listData = list[listKey];
    
    const text = `List: ${listKey}\nType: ${list.type || 'Unknown'}\nItems: ${listData.length}\n\nItems:\n${listData.map((item, idx) => `${idx + 1}. ${item.text || item.title || 'Unnamed'}`).join('\n')}`;
    
    navigator.clipboard.writeText(text)
      .then(() => alert('List copied to clipboard!'))
      .catch(() => alert('Failed to copy list'));
  };

  const toggleListExpansion = (listKey) => {
    setExpandedListId(expandedListId === listKey ? null : listKey);
  };

  const handleDependentChange = (listId, depKey, value) => {
    const listObj = lists.find((l) => l.id === listId);
    if (!listObj) return;
    const listKey = Object.keys(listObj)[0];
    const updatedList = {
      [listKey]: listObj[listKey],
      ...listObj,
      [depKey]: value || undefined,
    };
    if (!updatedList.dependent1 && !updatedList.dependent2) {
      delete updatedList.dependent1;
      delete updatedList.dependent2;
    }
    onListUpdate?.(updatedList);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="all-lists-modal"
      overlayClassName="all-lists-overlay"
      ariaHideApp={false}
    >
      <div className="modal-header">
        <h2>
          <FontAwesomeIcon icon={faListCheck} style={{ marginRight: "10px" }} />
          All Lists ({lists.length})
        </h2>
        <ExcelImportExport
          lists={lists}
          filteredLists={filteredLists}
          sdataGroup={sdataGroup}
          onListUpdate={onListUpdate}
        />
        <button className="close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Search and rest of the modal unchanged */}
      <div className="search-section">
        <div className="search-container">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search lists by name or question number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="lists-container">
        {filteredLists.length === 0 ? (
          <div className="no-lists-message">
            <FontAwesomeIcon icon={faQuestionCircle} size="3x" style={{ color: "#9ca3af", opacity: 0.5 }} />
            <p>No lists found.</p>
          </div>
        ) : (
          filteredLists.map((list, index) => {
            const listKey = Object.keys(list)[0];
            const listData = list[listKey];
            const isExpanded = expandedListId === listKey;

            return (
              <div key={index} className="list-card">
                <div
                  className="list-header"
                  onClick={() => toggleListExpansion(listKey)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleListExpansion(listKey);
                    }
                  }}
                >
                  <div className="list-title-container">
                    <div>
                      <h3 className="list-title">{listKey}</h3>
                      <div className="list-meta">
                        <span className="list-meta-item">
                          <strong>Type:</strong>
                          <span className={`list-type-badge ${getTypeBadgeClass(list)}`}>
                            {list.type || "unknown"}
                          </span>
                        </span>
                        <span className="list-meta-item">
                          <strong>Items:</strong> {Array.isArray(listData) ? listData.length : 0}
                        </span>
                        <span className="list-meta-item">
                          <strong>Used in:</strong> {list.questionNumbers?.length || 0} questions
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="list-expand-btn"
                    onClick={(e) => { e.stopPropagation(); toggleListExpansion(listKey); }}
                  >
                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                  </button>
                </div>

                {isExpanded && (
                  <div className="list-content">
                    <div className="questions-section">
                      <div className="section-title">Dependent Variables</div>
                      <div className="dependent-dropdowns">
                        <div className="dependent-dropdown">
                          <label>Dependent 1:</label>
                          <select
                            value={list.dependent1 || ""}
                            onChange={(e) => handleDependentChange(list.id, "dependent1", e.target.value)}
                          >
                            <option value="">— None —</option>
                            {eligibleQuestions.map((q) => (
                              <option key={q.number} value={q.number}>
                                {q.number} - {q.title}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="dependent-dropdown">
                          <label>Dependent 2:</label>
                          <select
                            value={list.dependent2 || ""}
                            onChange={(e) => handleDependentChange(list.id, "dependent2", e.target.value)}
                          >
                            <option value="">— None —</option>
                            {eligibleQuestions.map((q) => (
                              <option key={q.number} value={q.number}>
                                {q.number} - {q.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {list.questionNumbers && list.questionNumbers.length > 0 && (
                      <div className="questions-section">
                        <div className="section-title">Used in Questions:</div>
                        <div className="questions-grid">{renderQuestionTags(list)}</div>
                      </div>
                    )}

                    <div className="list-items-section">
                      <div className="section-title">List Items:</div>
                      <div className="items-container">{renderListItems(list)}</div>
                    </div>

                    <div className="actions-section">
                      <button className="action-button primary" onClick={() => copyListToClipboard(list)}>
                        <FontAwesomeIcon icon={faCopy} />
                        Copy List Details
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
};

export default AllListsModal;